import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// PRD-007 §7 — unsubscribe confirmation page. Renders three things:
//   1. Confirmation: "Notifications paused."
//   2. Undo CTA: restores previous state. Critical because mis-clicked
//      unsubscribes are common; the recovery path should be one click.
//   3. Link to account-level granular preferences.
//
// Public route — no auth. The token in the URL is its own bearer.
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'invalid' });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/unsubscribe`, { params: { token } });
        setState({ status: 'paused', email: r.data.email });
      } catch (err) {
        setState({
          status: 'invalid',
          message: err.response?.data?.message || 'This unsubscribe link is invalid or has expired.',
        });
      }
    })();
  }, [token]);

  const onUndo = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      await axios.post(`${API_BASE_URL}/api/unsubscribe/undo`, { token });
      setState((s) => ({ status: 'resumed', email: s.email }));
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: err.response?.data?.error_code || err.message }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-md shadow-sm p-6">
        <div className="text-sm font-semibold text-gray-900 mb-3">Verdict</div>
        {state.status === 'loading' && <p className="text-gray-600 text-sm">Updating your preferences…</p>}

        {state.status === 'invalid' && (
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">This link didn't work</h1>
            <p className="text-sm text-gray-600 mb-4">
              {state.message || 'The unsubscribe link is invalid or has expired.'}
            </p>
            <Link to="/account/notifications" className="text-sm text-primary-600 hover:underline">
              Sign in to manage notification preferences
            </Link>
          </>
        )}

        {state.status === 'paused' && (
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Notifications paused</h1>
            <p className="text-sm text-gray-600 mb-4">
              We won't send transactional email to <strong>{state.email}</strong> until you resume notifications.
            </p>
            <div className="flex gap-2">
              <button
                onClick={onUndo}
                disabled={state.busy}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 rounded-md"
              >
                {state.busy ? 'Resuming…' : 'Resume notifications'}
              </button>
              <Link
                to="/account/notifications"
                className="px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Manage preferences
              </Link>
            </div>
            {state.error && <div className="mt-3 text-sm text-red-700">{state.error}</div>}
          </>
        )}

        {state.status === 'resumed' && (
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Notifications resumed</h1>
            <p className="text-sm text-gray-600">
              We'll continue sending transactional email to <strong>{state.email}</strong>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
